import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { ChevronLeft, ChevronRight, PlayCircle, Loader2, Upload, Download, Check, Plus } from "lucide-react";
import AppHeader from "../components/AppHeader";
import ProjectInfoTab from "../components/wizard/ProjectInfoTab";
import RoomDetailsTab, { blankRoom } from "../components/wizard/RoomDetailsTab";
import DesignConditionsTab from "../components/wizard/DesignConditionsTab";
import SelectionCriteriaTab from "../components/wizard/SelectionCriteriaTab";
import SummaryTab from "../components/wizard/SummaryTab";
import ConfiguratorTab from "../components/wizard/ConfiguratorTab";
import AssignmentTab from "../components/wizard/AssignmentTab";
import SubmittalPackageTab from "../components/wizard/SubmittalPackageTab";
import SaveLoadProject from "../components/SaveLoadProject";
import { runRoomSelection, extractMultiLengthSummary, bumpForLength, bumpForWaterFlow, bumpForStaticPressure, bumpForPAFlow, bumpForSpigot, isModelN } from "../lib/wizardCalc";
import { parseRoomsFile, downloadRoomsTemplate, downloadRoomsTemplateSI } from "../lib/roomsImport";
import { useAuth, projectApi, formatApiError } from "../lib/auth";
import { listProjectReports } from "../lib/reportsApi";

// B.1.6: "Selection Criteria" inserted after Design Conditions (index 3).
// Step indices:
//   0 = Project Info
//   1 = Room Details
//   2 = Design Conditions
//   3 = Selection Criteria   ← NEW
//   4 = Summary
//   5 = Configurator
//   6 = Schedule
//   7 = Submittal
const STEPS = [
  { key: "project", label: "Project" },
  { key: "rooms", label: "Room Details" },
  { key: "design", label: "Design Conditions" },
  { key: "criteria", label: "Selection Criteria" },
  { key: "summary", label: "Summary" },
  { key: "configurator", label: "Configurator" },
  { key: "assignment", label: "Schedule" },
  { key: "submittal", label: "Submittal" },
];

export default function ProjectWizard() {
  const [step, setStep] = useState(0);
  const fileInputRef = React.useRef(null);

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const { rooms: parsed, errors, warnings } = await parseRoomsFile(file);
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      setRooms(parsed);
      // Imported rooms wholesale replace the previous list — old results and
      // configurations no longer correspond to any room by index.
      setResults([]);
      setConfigurations([]);
      setInputsVersion((v) => v + 1);
      toast.success(
        warnings.length > 0
          ? `Imported ${parsed.length} room(s) — ${warnings.length} warning(s)`
          : `Imported ${parsed.length} room(s)`
      );
      warnings.slice(0, 3).forEach((w) => toast.warning(w, { duration: 6000 }));
    } catch (err) {
      toast.error("Failed to parse file: " + (err.message || "unknown"));
    }
  };
  const [project, setProject] = useState({
    name: "",
    number: "",
    address: "",
    cityText: "",
    state: "",
    // B.1.5 — project-wide sizing controls.
    // AHRI 1335 single-basis lock: capacity_basis is hardcoded to "room_load"
    // in wizardCalc.js payload builder. This field is kept in project state for
    // round-trip compatibility with saved projects; the payload overrides it.
    capacity_basis: "room_load",
    facb_strategy: "lowest_speed",
    acb_strategy: "given_flow_given_sp",
    // B.6 — water-side limits. max_water_dp null → backend defaults to 20 ftH2O.
    // water_flow_cap_mode "off" → no GPM cap sent; backend enforces hydraulic ceiling only.
    max_water_dp: null,
    water_flow_cap_mode: "off",
    water_flow_cap_value: null,
    designConditions: {
      country: "",
      city: "",
      outdoor_db_f: "",
      outdoor_wb_f: "",
      outdoor_db_winter_f: "",
      altitude_ft: "0",
      room_db_f: "75",
      room_rh: "50",
      room_db_winter_f: "70",
      room_rh_winter: "30",
      water_entering_f: "45",
      water_entering_winter_f: "120",
    },
    advanced: {
      length_ft: 4,
      use_glycol: false,
      glycol_type: "PG",
      glycol_percentage: "",
      o2_circuit: "serial",
      recovery_type: "HRV",
      non_condensing_mode: false,
      water_flow_gpm: "",
      air_on_temp_f: "",
      air_on_temp_heating_f: "",
      pa_eat_db_f: "",
      pa_eat_rh: "",
      pa_flow_cfm: "",
      pa_pressure_in_wg: "",
      acb_fan_speed: "MED",
    },
  });
  const [rooms, setRooms] = useState([blankRoom()]);
  const [results, setResults] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [running, setRunning] = useState(false);
  // Per-row recalc loading state. A Set of room indices currently being
  // re-run (water flow, static pressure, or length override). SummaryTab reads
  // this to disable that row's inputs and show a spinner.
  const [rowLoading, setRowLoading] = useState(() => new Set());
  const setRowBusy = useCallback((roomIdx, busy) => {
    setRowLoading((prev) => {
      const next = new Set(prev);
      if (busy) { next.add(roomIdx); } else { next.delete(roomIdx); }
      return next;
    });
  }, []);
  // Staleness tracking: increment inputsVersion whenever design conditions,
  // advanced settings, or rooms change. Store resultsVersion at the time
  // results were last computed. resultsStale = resultsVersion !== inputsVersion.
  const [inputsVersion, setInputsVersion] = useState(0);
  const [resultsVersion, setResultsVersion] = useState(0);
  // Global IP/SI unit-system toggle. Values are always stored in IP under
  // the hood (backend math is IP-only); this flag only affects display +
  // input formatting from Room Details onward.
  const [isSI, setIsSI] = useState(false);
  // Persistence: id of the loaded project (null = unsaved). When non-null,
  // Save → Update.
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  // Track which reports have been generated + saved to the backend for this
  // project. Map shape: { selection: ReportOut, config: ReportOut,
  // submittal: ReportOut }. Used to gate Next on Summary/Schedule and to
  // hydrate the Submittal tab.
  const [reports, setReports] = useState({});

  const refreshReports = useCallback(async (pid) => {
    const id = pid || currentProjectId;
    if (!id) return;
    try {
      const rows = await listProjectReports(id);
      const byKind = {};
      (rows || []).forEach((r) => { byKind[r.kind] = r; });
      setReports(byKind);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Failed to refresh project reports:", e);
    }
  }, [currentProjectId]);

  const { user, loading: authLoading, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Guests are redirected to /login. Defer the redirect render until after
  // all hooks have been declared (React requires identical hook order).
  const guestRedirect = !authLoading && !user;

  // Refs that always hold the latest slice of wizard state. Updated
  // synchronously after every render via the useEffects below. getWizardState
  // reads from these refs so the autoSave callback captures the post-setState
  // values regardless of when the 600ms timer fires.
  const projectRef = useRef(project);
  const roomsRef = useRef(rooms);
  const resultsRef = useRef(results);
  const configurationsRef = useRef(configurations);
  const stepRef = useRef(step);
  const isSIRef = useRef(isSI);
  useEffect(() => { projectRef.current = project; }, [project]);
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);
  useEffect(() => { resultsRef.current = results; }, [results]);
  useEffect(() => { configurationsRef.current = configurations; }, [configurations]);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { isSIRef.current = isSI; }, [isSI]);

  // Bump inputsVersion whenever design conditions, advanced settings, or rooms
  // change so SummaryTab can show a stale-results banner. Skip on mount and
  // during project hydration (isHydratingRef) — those changes are loading a
  // matched state, not user edits.
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current || isHydratingRef.current) return;
    if (!resultsRef.current || resultsRef.current.length === 0) return;
    setInputsVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.designConditions, project.advanced]);
  useEffect(() => {
    if (isFirstRenderRef.current || isHydratingRef.current) return;
    if (!resultsRef.current || resultsRef.current.length === 0) return;
    setInputsVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);
  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);

  // Snapshot of full wizard state for persistence — always reads from refs so
  // it returns the current state even when called inside a stale closure.
  // results[].raw is stripped so autosave payloads stay small (raw is
  // re-fetchable; summary has everything downstream views need to render).
  const getWizardState = () => {
    const sanitizedResults = (resultsRef.current || []).map((r) => {
      if (!r || typeof r !== "object") return r;
      const { raw, ...rest } = r; // eslint-disable-line no-unused-vars
      return rest;
    });
    return {
      project: projectRef.current,
      rooms: roomsRef.current,
      results: sanitizedResults,
      configurations: configurationsRef.current,
      step: stepRef.current,
      isSI: isSIRef.current,
      schema_version: 3,
    };
  };
  const setWizardState = (state) => {
    if (!state || typeof state !== "object") return;
    if (state.project) {
      // B.1.5 — silently default the new fields when loading an older saved
      // project that pre-dates this release. This must NOT trigger a recalc
      // (isHydratingRef suppresses the stale-results useEffect during hydration).
      // AHRI 1335 lock: capacity_basis default is "room_load"; legacy saved
      // "water_side" is overridden by ...state.project spread, but the payload
      // builder in wizardCalc.js hardcodes "room_load" regardless.
      setProject({
        capacity_basis: "room_load",
        facb_strategy: "lowest_speed",
        acb_strategy: "given_flow_given_sp",
        // B.6 — silently default water-side limit fields for legacy saves.
        max_water_dp: null,
        water_flow_cap_mode: "off",
        water_flow_cap_value: null,
        ...state.project,
      });
    }
    if (Array.isArray(state.rooms) && state.rooms.length > 0) {
      // Backfill qty=1 for rooms saved before this field existed.
      setRooms(state.rooms.map((r) => (r.qty == null ? { ...r, qty: 1 } : r)));
    }
    if (Array.isArray(state.results)) {
      // Heal historical qty drift: prior versions clamped plan.qty to 99 even
      // when the user typed a larger value in Room Details. room.qty is the
      // source of truth — sync plan.qty to it on load so Summary reflects what
      // the engineer sees in Room Details. The unit array is also patched so
      // BOM / configurations stay coherent.
      const rooms2 = state.rooms || [];
      const healed = state.results.map((r, i) => {
        const roomQty = parseInt(rooms2[i]?.qty, 10);
        if (!r?.ok || !Number.isFinite(roomQty) || roomQty < 1) return r;
        const planQty = r?.summary?.plan?.qty;
        if (planQty === roomQty) return r;
        const oldPlan = r.summary?.plan || {};
        const oldUnits = Array.isArray(oldPlan.units) ? oldPlan.units : [];
        const newUnits = oldUnits.length === 1
          ? [{ ...oldUnits[0], qty: roomQty }]
          : oldUnits.length > 1
            ? (() => {
                const others = oldUnits.slice(1).reduce((s, u) => s + (u.qty || 1), 0);
                const head = Math.max(1, roomQty - others);
                return [{ ...oldUnits[0], qty: head }, ...oldUnits.slice(1)];
              })()
            : oldUnits;
        return {
          ...r,
          summary: {
            ...r.summary,
            plan: { ...oldPlan, qty: roomQty, units: newUnits },
          },
        };
      });
      setResults(healed);
    }
    if (Array.isArray(state.configurations)) setConfigurations(state.configurations);
    if (typeof state.step === "number") setStep(state.step);
    if (typeof state.isSI === "boolean") setIsSI(state.isSI);
  };

  // Load project from ?projectId= on mount (or when auth resolves)
  const loadedFromUrlRef = useRef(false);
  const pendingStaleRecalcRef = useRef(false);
  useEffect(() => {
    if (loadedFromUrlRef.current) return;
    if (authLoading) return;
    const pid = searchParams.get("projectId");
    if (!pid || !user) return;
    loadedFromUrlRef.current = true;
    // Suspend background auto-save until the loaded state is fully in place.
    // Bumping the generation counter here cancels any in-flight debounce timer
    // that captured the pre-hydration generation (Fix C9: 100ms guard < 600ms
    // debounce created a window where a stale save could fire mid-hydration).
    isHydratingRef.current = true;
    autoSaveGenRef.current += 1;
    projectApi
      .get(pid)
      .then((proj) => {
        setWizardState(proj.payload || {});
        // Always land on Step 1 (Project Info) when opening a project from Home,
        // so engineers can verify project details before navigating forward.
        setStep(0);
        setCurrentProjectId(proj.id);
        setProject((prev) => ({ ...(proj.payload?.project || prev), name: proj.name }));
        // Pull the persisted report metadata for this project so the
        // Submittal tab can hydrate without re-running heavy generators.
        refreshReports(proj.id);
        toast.success(`Loaded "${proj.name}"`);
        // Re-enable auto-save on the next tick so the hydrating state-flushes
        // settle before the watcher effect runs.
        setTimeout(() => { isHydratingRef.current = false; }, 100);
        // Detect stale psychrometric cache from pre-PA-support saves:
        // any PA-model room whose cached chart lacks the PA point. If found,
        // queue a transparent auto-rerun so users immediately see the
        // corrected PA + mixing visualization without manually clicking
        // Optimize.
        const PA_MODELS = ["Model L-L", "Model L-T", "Model L-F", "Model I-T", "Model I-F", "Model C", "Model C1", "Model D", "Model H"];
        const savedRooms = proj.payload?.rooms || [];
        const savedResults = proj.payload?.results || [];
        const needsRecalc = savedRooms.some((r, i) => {
          const pts = savedResults[i]?.raw?.results?.[0]?.cooling?.psych_chart?.points;
          if (!pts) return false;
          // (a) Pre-PA-support save: PA-capable model missing the PA point.
          if (PA_MODELS.includes(r?.unit_model) && !pts.PA) return true;
          // (b) Stale humidity bug: AO (LAT) cannot have higher absolute
          //     humidity than RA on a cooling chart — cooling never adds
          //     moisture. Old caches (pre-2026-05-16) hit this when the coil
          //     was dry and we overwrote W with w_sat(T_leaving).
          const ra = pts.RA, ao = pts.AO;
          if (ra && ao && typeof ra.w_grains === "number" && typeof ao.w_grains === "number") {
            if (ao.w_grains > ra.w_grains + 1) return true; // 1 gr tolerance
          }
          return false;
        });
        if (needsRecalc) {
          pendingStaleRecalcRef.current = true;
          toast.info("Refreshing selections to update psychrometric charts…", { duration: 2500 });
        }
      })
      .catch((err) => toast.error("Could not load project: " + formatApiError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  // Effect: when stale-recalc is pending AND rooms have loaded, kick off runAll.
  useEffect(() => {
    if (!pendingStaleRecalcRef.current) return;
    if (running) return;
    if (!Array.isArray(rooms) || rooms.length === 0) return;
    pendingStaleRecalcRef.current = false;
    // Use a short delay so toast renders before the blocking recalc starts.
    const t = setTimeout(() => { runAll(false); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, running]);

  // Auto-save: triggered after selections run and after PDF generation.
  // Skip if user is not signed in.
  // Falls back to "Untitled — <date>" name when project.name is empty so we
  // never block auto-save just because the user hasn't typed a name yet.
  const autoSaveRef = useRef({ inflight: false });
  // getWizardState reads from refs so this callback never captures stale state.
  // Only stable / identity-stable values remain in the dep array.
  const autoSave = useCallback(async () => {
    if (!user) return;
    if (autoSaveRef.current.inflight) return; // single-flight
    autoSaveRef.current.inflight = true;
    setAutoSaving(true);
    try {
      const fallback = `Untitled — ${new Date().toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}`;
      const name = (projectRef.current?.name || "").trim() || fallback;
      const payload = getWizardState();
      if (currentProjectId) {
        await projectApi.update(currentProjectId, name, payload);
      } else {
        const created = await projectApi.create(name, payload);
        setCurrentProjectId(created.id);
        // Reflect fallback name into UI so user sees what was saved
        if (!projectRef.current?.name) {
          setProject((p) => ({ ...p, name: created.name }));
        }
        // Push the new id into the URL so refresh reloads the same project
        navigate(`/wizard?projectId=${created.id}`, { replace: true });
      }
    } catch (err) {
      toast.error("Auto-save failed: " + formatApiError(err));
    } finally {
      autoSaveRef.current.inflight = false;
      setAutoSaving(false);
    }
  }, [user, currentProjectId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always-fresh autoSave ref. Callbacks created earlier in a render (e.g.
  // `onUpdateQty` passed deep into SummaryTab) capture the autoSave that
  // existed at THAT render. By the time setTimeout fires, results have been
  // updated and a newer autoSave (with fresh closure) is available. We
  // forward all autoSave calls through this ref so persistence always uses
  // the latest results / project state.
  const autoSaveFnRef = useRef(autoSave);
  useEffect(() => { autoSaveFnRef.current = autoSave; }, [autoSave]);
  const autoSaveLatest = useCallback(() => autoSaveFnRef.current?.(), []);

  // Continuous background persistence — whenever the user edits project info,
  // rooms, or configurations, push the latest state to the DB after a short
  // idle period. This guarantees nothing is lost when the user navigates
  // away mid-edit. The autoSave function itself is single-flight, so even
  // bursty edits never stack up overlapping requests.
  //
  // Gating:
  //   • only when logged in (no backend save for guests)
  //   • only after the user has actually typed something — either a project
  //     name, or non-empty room data — so simply landing on /wizard does NOT
  //     create a phantom "Untitled" record.
  //   • paused while loading an existing project (isHydratingRef).
  //
  // Fix C9 (autosave hydration race): a generation counter guards against a
  // pre-hydration debounce timer firing after hydration has started. Each
  // effect run captures the current generation; the scheduled callback
  // checks that the generation hasn't been bumped (by hydration) before
  // committing the save. 100ms guard < 600ms debounce created the window;
  // this eliminates it without extending the visible hydration delay.
  const isHydratingRef = useRef(false);
  const autoSaveGenRef = useRef(0);
  useEffect(() => {
    if (!user) return;
    if (isHydratingRef.current) return;
    const hasName = !!(project?.name && project.name.trim());
    const hasRoomEdits = (rooms || []).some(
      (r) => r && (r.unit_model || r.cooling_total_btuh || r.cooling_sensible_btuh || (r.name && r.name.trim()))
    );
    // Once a project_id exists we always keep saving (otherwise we'd silently
    // drop edits made after the user clears the name field, for example).
    if (!currentProjectId && !hasName && !hasRoomEdits) return;
    const gen = autoSaveGenRef.current;
    const t = setTimeout(() => {
      // If a hydration started after this timer was scheduled, skip the save.
      if (autoSaveGenRef.current !== gen) return;
      autoSaveLatest();
    }, 600);
    return () => clearTimeout(t);
  }, [project, rooms, results, configurations, isSI, user, autoSaveLatest, currentProjectId]);

  // Whether to show Advanced tab — ALWAYS show but skip-button when single model
  const distinctModels = useMemo(
    () => [...new Set(rooms.map((r) => r.unit_model).filter(Boolean))],
    [rooms]
  );
  const validateStep = (s) => {
    if (s === 0) {
      if (!project.name) {
        toast.error("Project Name is required");
        return false;
      }
    }
    if (s === 1) {
      if (rooms.length === 0) {
        toast.error("Add at least one room");
        return false;
      }
      const firstInvalid = rooms.findIndex(
        (r) => !r.unit_model || (!r.cooling_total_btuh && !r.cooling_sensible_btuh)
      );
      if (firstInvalid >= 0) {
        toast.error(
          `Room ${firstInvalid + 1}: Unit Model and Cooling load (Total or Sensible) are required`
        );
        return false;
      }
    }
    if (s === 2) {
      const dc = project.designConditions;
      // Room DB is always needed; outdoor DB + water inlet are only required
      // when at least one room uses a model that consumes outdoor air
      // (Model N / N-f). Induction-only projects hide those fields in the UI
      // so requiring them here would block the user with no way to fix it.
      const needsOutdoor = (rooms || []).some((r) => isModelN(r.unit_model));
      const missing = [];
      if (!dc.room_db_f) missing.push("Room DB");
      if (needsOutdoor) {
        if (!dc.outdoor_db_f) missing.push("Outdoor DB");
        if (!dc.water_entering_f) missing.push("Water inlet");
      }
      if (missing.length) {
        toast.error(`${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} required`);
        return false;
      }
    }
    return true;
  };

  const goNext = async () => {
    if (!validateStep(step)) return;
    // Persist current state to the DB immediately on every Next. This
    // guarantees the brand-new project is created the moment the user leaves
    // the Project tab (step 0 → step 1) and that every subsequent edit is
    // checkpointed even if the user reloads or closes the tab.
    if (user) {
      try { await autoSave(); } catch (e) { /* autoSave surfaces its own toast */ }
    }
    // After Selection Criteria tab (step 3) → run calculations and jump to Summary (step 4).
    // (Design Conditions is step 2 — stepping through it lands on Selection Criteria.)
    if (step === 3) {
      await runAll();
      setStep(4);
      return;
    }
    // Leaving Summary → freeze the current selection schedule and clear
    // orphaned configurations (configurations whose unit_model+length_ft
    // pair is no longer represented in the active selection). User's
    // length overrides take precedence; any old configs for those rooms
    // pointing at the previous length are deleted.
    if (step === 4) {
      const activeKeys = new Set();
      (rooms || []).forEach((r, i) => {
        const res = results?.[i];
        if (!res?.ok || !r.unit_model) return;
        // Collect EVERY length present in the active plan (including
        // multi-unit splits where sub-units may have different lengths).
        const plan = res?.summary?.plan;
        const planLens = new Set();
        if (plan?.units && Array.isArray(plan.units)) {
          plan.units.forEach((u) => { if (u?.length_ft != null) planLens.add(u.length_ft); });
        }
        if (plan?.length_ft != null) planLens.add(plan.length_ft);
        // Fix #3: also include any length the user explicitly overrode, even
        // if the engine (transiently) returned a different value. Belt-and-
        // suspenders against Fix #1 escaping under edge conditions.
        if (plan?.overridden === true && plan?.length_ft != null) {
          planLens.add(plan.length_ft);
        }
        if (planLens.size === 0) {
          const fallback = res?.summary?.selected_length_ft ?? res?.raw?.results?.[0]?.length_ft;
          if (fallback != null) planLens.add(fallback);
        }
        planLens.forEach((len) => activeKeys.add(`${r.unit_model}__${len}`));
      });
      const allConfigs = configurations || [];
      const filtered = allConfigs.filter((cfg) => {
        if (!cfg?.unit_model) return false;
        // Length-agnostic configs (Model N etc.) keep their key as just model.
        const key = cfg.length_ft != null ? `${cfg.unit_model}__${cfg.length_ft}` : cfg.unit_model;
        const keyWithLen = cfg.length_ft != null ? key : null;
        return activeKeys.has(key) || (keyWithLen == null && [...activeKeys].some((k) => k.startsWith(`${cfg.unit_model}__`)));
      });
      const deletedCount = allConfigs.length - filtered.length;
      if (deletedCount > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `[goNext step 3] Clearing ${deletedCount} stale configuration(s). ` +
          `Active keys: [${[...activeKeys].join(", ")}]. ` +
          `Removed: [${allConfigs.filter((c) => !filtered.includes(c)).map((c) => `${c.unit_model}__${c.length_ft}`).join(", ")}]`
        );
        setConfigurations(filtered);
        toast.info(`Cleared ${deletedCount} stale configuration${deletedCount === 1 ? "" : "s"} (length changed).`);
      }
      setSelectionMode("accepted");
      if (user) setTimeout(() => autoSaveLatest(), 100);
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  // selectionMode tracks which phase the user is in:
  //   "initial"   = first pass, given PA + max GPM + cap, NO PA-bump escalation
  //   "optimized" = user clicked Optimize → bump-loop + cap-relax cascade
  //   "individual"= user opted to manually pick per-room lengths
  //   "accepted"  = user accepted the current selection
  const [selectionMode, setSelectionMode] = useState("initial");

  // resultsStale: true when inputs have changed since the last runAll. Drives
  // the stale banner in SummaryTab and also resets selectionMode to "initial"
  // so the Optimize button correctly reflects invalidated results.
  const resultsStale = results.length > 0 && inputsVersion !== resultsVersion;
  useEffect(() => {
    if (resultsStale) {
      setSelectionMode("initial");
    }
  }, [resultsStale]);

  const runAll = async (optimize = false) => {
    setRunning(true);
    try {
      // Run rooms in parallel with a concurrency cap of 6. Sequential was
      // ~3-5s per room → 2+ min for 40 rooms; parallel keeps it under 30s
      // while still avoiding backend overload.
      const out = new Array(rooms.length);
      const CONCURRENCY = 6;
      let next = 0;
      const workers = Array.from({ length: Math.min(CONCURRENCY, rooms.length) }, async () => {
        while (true) {
          const i = next++;
          if (i >= rooms.length) return;
          out[i] = await runRoomSelection(project, rooms[i], optimize);
        }
      });
      await Promise.all(workers);
      // Snapshot manual qty overrides BEFORE clearing — rooms where the
      // engineer explicitly set qty_mode (overridden=true) retain their qty
      // and mode after the re-run. Rooms without an override are re-prompted.
      const priorOverrides = (resultsRef.current || []).map((r) => {
        const plan = r?.summary?.plan;
        if (!plan?.overridden || !plan?.qty_mode) return null;
        return { qty: plan.qty, qty_mode: plan.qty_mode };
      });
      // Engine has produced a fresh selection — clear the project-wide default
      // so the engineer is re-prompted for any NEW multi-unit rooms that don't
      // already have an explicit per-room override.
      setProject((p) => {
        const np = { ...p };
        delete np.default_qty_mode;
        return np;
      });
      const cleared = out.map((r, i) => {
        if (!r?.ok) return r;
        const plan = r.summary?.plan;
        const override = priorOverrides[i];
        if (override) {
          // Re-apply the engineer's manual qty + mode onto the fresh engine result.
          return {
            ...r,
            summary: {
              ...r.summary,
              plan: {
                ...plan,
                qty: override.qty,
                qty_mode: override.qty_mode,
                overridden: true,
              },
            },
          };
        }
        // Strip qty_mode only when the plan was AUTO-derived (no explicit
        // override). When overridden=true the qty came from a deliberate
        // source — Room Details room.qty > 1 (which routes through
        // runRoomSelection's effectiveQty/effectiveQtyMode path) or a prior
        // Summary user edit captured via priorOverrides. In both cases the
        // qty_mode is data-driven and must survive across re-runs so the
        // Delivered % column uses the right multiplier (mult=1 for
        // duplicate, mult=qty for divide).
        if (!plan?.qty_mode || plan?.overridden) return r;
        const { qty_mode: _drop, ...restPlan } = plan;
        return { ...r, summary: { ...r.summary, plan: restPlan } };
      });
      setResults(cleared);
      // Snapshot the current inputsVersion so resultsStale clears.
      setResultsVersion(inputsVersion);
      setSelectionMode(optimize ? "optimized" : "initial");
      const okCount = out.filter((r) => r && r.ok).length;
      toast.success(
        `Selection ${optimize ? "optimized" : "complete"}: ${okCount}/${rooms.length} rooms`
      );
      // Auto-save after a successful selection run (signed-in users only)
      if (user) {
        setTimeout(() => autoSaveLatest(), 100);
      }
    } catch (err) {
      toast.error("Selection failed: " + (err.message || "unknown"));
    } finally {
      setRunning(false);
    }
  };

  // Debounce timer for cap-driven recalcs triggered by DesignConditionsTab
  // onMaxLengthChange prop. Stored on a ref so we can clear it across
  // re-renders. `runAllRef` keeps a pointer to the LATEST runAll closure so
  // the debounced callback doesn't fire with a stale `project` snapshot
  // (state updates and the cap-change callback both happen in the same tick).
  const capDebounceRef = useRef(null);
  const runAllRef = useRef(runAll);
  useEffect(() => {
    runAllRef.current = runAll;
  });

  // Shared "update a single room's plan.qty" — used by both SummaryTab
  // (per-room qty editor) and AssignmentTab (Schedule edit on a single-
  // variant room propagates back to the source-of-truth room.qty).
  //
  // Two effects fire whenever the user changes QTY:
  //   (1) Synchronous BOM bump — immediately stamp the new qty into
  //       results[i].summary.plan.units and configurations.assignments so
  //       the schedule / variant summary update without a network round-trip.
  //   (2) Async re-selection — re-run runRoomSelection() with forceQty=q so
  //       the engine picks the correct length for that qty constraint. For
  //       example, dropping from auto-selected qty=2 to qty=1 should bump
  //       the picker to a longer length (or surface a negative Delivered %
  //       if no length within the cap can meet the full room load). Result
  //       is merged into results[i] when it returns; toast confirms.
  const updateRoomQty = useCallback((roomIdx, newQty, qtyMode = "divide") => {
    const q = Math.max(1, parseInt(newQty, 10) || 1);
    // Mirror the qty back to the room definition so Room Details stays in sync.
    setRooms((prev) => prev.map((r, i) => i === roomIdx ? { ...r, qty: q } : r));
    setResults((prev) => {
      const next = [...prev];
      const c = next[roomIdx];
      if (!c?.ok) return prev;
      const oldPlan = c.summary?.plan || {};
      const oldUnits = (oldPlan.units && oldPlan.units.length) ? oldPlan.units : null;
      const duplicate = qtyMode === "duplicate";
      const fullRoomTotal = parseFloat(roomsRef.current?.[roomIdx]?.cooling_total_btuh) || 0;
      const fullRoomOa = parseFloat(roomsRef.current?.[roomIdx]?.oa_flow_cfm) || 0;
      let newUnits;
      if (oldUnits && oldUnits.length > 1) {
        const others = oldUnits.slice(1).reduce((s, u) => s + (u.qty || 1), 0);
        const head = Math.max(1, q - others);
        newUnits = [{ ...oldUnits[0], qty: head }, ...oldUnits.slice(1)];
      } else {
        const base = oldUnits ? oldUnits[0] : {
          model: roomsRef.current?.[roomIdx]?.unit_model,
          length_ft: oldPlan.length_ft,
          per_unit_target: fullRoomTotal,
          per_unit_total: c.summary.cooling_total_btuh,
          per_unit_oa: fullRoomOa,
        };
        // Apply qty-mode optimistic stamp so the row updates immediately even
        // before the async re-selection lands.
        if (duplicate) {
          newUnits = [{
            ...base,
            qty: q,
            per_unit_target: fullRoomTotal,
            per_unit_oa: fullRoomOa,
          }];
        } else {
          newUnits = [{
            ...base,
            qty: q,
            per_unit_target: q > 0 ? fullRoomTotal / q : fullRoomTotal,
            per_unit_oa: q > 0 ? fullRoomOa / q : fullRoomOa,
          }];
        }
      }
      next[roomIdx] = {
        ...c,
        summary: {
          ...c.summary,
          plan: {
            ...oldPlan, qty: q, units: newUnits,
            qty_mode: qtyMode,
            applied: q > 1 || (newUnits && newUnits.length > 1),
            overridden: true,
          },
        },
      };
      return next;
    });
    // Mirror the qty change into configurations.assignments for any
    // single-variant assignment on this room. Multi-variant splits are
    // left untouched so the user can manually rebalance.
    // Use room_id (schema v3) when available; fall back to room_idx.
    const roomId = roomsRef.current?.[roomIdx]?.id;
    setConfigurations((prev) => {
      if (!prev || !prev.length) return prev;
      const touching = prev
        .map((c) => ({
          c,
          a: (c.assignments || []).find((a) =>
            roomId != null && a.room_id != null
              ? a.room_id === roomId           // prefer stable id (schema v3)
              : a.room_idx === roomIdx          // fallback for older payloads
          ),
        }))
        .filter((x) => x.a && x.a.qty > 0);
      if (touching.length !== 1) return prev;
      const targetCfg = touching[0].c;
      // Same group_key may have multiple variant configs but only one is
      // assigned to this room (touching.length === 1). Bump only that one.
      return prev.map((c) => {
        if (c.id !== targetCfg.id) return c;
        const others = (c.assignments || []).filter((a) =>
          roomId != null && a.room_id != null
            ? a.room_id !== roomId
            : a.room_idx !== roomIdx
        );
        // Write room_id as primary; keep room_idx for transition period.
        const newAssignment = roomId != null
          ? { room_id: roomId, room_idx: roomIdx, qty: q }
          : { room_idx: roomIdx, qty: q };
        return { ...c, assignments: [...others, newAssignment] };
      });
    });
    // (2) Re-run the engine with the forced qty so length & capacities
    // are re-derived. We use the current room snapshot, not a stale closure,
    // by reading from the `rooms` ref-like array directly inside the async
    // body. Errors are swallowed — the BOM stamp from (1) is still valid.
    //
    // DUPLICATE mode is special: the user is just stamping N independent
    // copies of the same unit on the room. Nothing about the per-unit
    // selection should change (same length, same coil, same delivered
    // capacity / OA / sensible / heating). So we skip the re-run entirely
    // and trust the optimistic plan update above. Only the DIVIDE branch
    // re-runs the engine to size each unit for the smaller per-unit target.
    if (qtyMode === "duplicate") {
      if (user) setTimeout(() => autoSaveLatest(), 100);
      return;
    }
    const room = roomsRef.current?.[roomIdx];
    if (room && room.unit_model) {
      // Read the user's length override BEFORE the async call so we can
      // constrain the engine to that length (Fix #1: V.23 cascade regression).
      // Read from resultsRef (not closure-captured `results`) so we always see
      // the post-override state — `updateRoomQty` is called via deep callback
      // chains where dep-array correctness is fragile.
      const currentPlan = resultsRef.current?.[roomIdx]?.summary?.plan;
      const userOverrideLength =
        currentPlan?.overridden === true && currentPlan?.length_ft != null
          ? currentPlan.length_ft
          : null;
      (async () => {
        try {
          const res = await runRoomSelection(projectRef.current, room, false, q, qtyMode, userOverrideLength);
          if (!res?.ok) return;
          setResults((prev) => {
            const next = [...prev];
            const cur = next[roomIdx];
            if (!cur) return prev;
            const prevPlan = cur.summary?.plan || {};
            // Preserve the user's manual qty + propagate auto-saved fields.
            const newPlan = {
              ...(res.summary?.plan || {}),
              qty: q,
              qty_mode: qtyMode,
              overridden: true,
            };
            // Belt-and-suspenders: if the user had a length override and the
            // engine returned a different length, force it back. This guards
            // against the backend ignoring forceLength under any edge condition.
            if (prevPlan.overridden === true && prevPlan.length_ft != null &&
                newPlan.length_ft !== prevPlan.length_ft) {
              // eslint-disable-next-line no-console
              console.warn(
                `[updateRoomQty] Engine returned length ${newPlan.length_ft} ft but user ` +
                `override is ${prevPlan.length_ft} ft — restoring override.`
              );
              newPlan.length_ft = prevPlan.length_ft;
              newPlan.overridden = true;
            }
            next[roomIdx] = {
              ...res,
              summary: {
                ...res.summary,
                plan: newPlan,
              },
            };
            return next;
          });
          if (user) setTimeout(() => autoSaveLatest(), 200);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`Re-selection for room ${roomIdx} (qty=${q}) failed:`, e);
        }
      })();
    }
    if (user) setTimeout(() => autoSaveLatest(), 100);
  }, [user, autoSaveLatest]);

  // Remove a single room by index, cleaning results[] and configurations[].
  // Primary identifier: room.id (stable UUID). room_idx re-indexing is kept
  // as a backward-compat fallback for payloads that pre-date schema_version 3.
  const handleRemoveRoom = useCallback((idx) => {
    const deletedRoom = roomsRef.current?.[idx];
    const deletedId = deletedRoom?.id;
    setRooms((prev) => prev.filter((_, i) => i !== idx));
    setResults((prev) => prev.filter((_, i) => i !== idx));
    setConfigurations((prev) => (prev || []).map((cfg) => ({
      ...cfg,
      assignments: (cfg.assignments || []).filter((a) => {
        // Prefer room_id (schema v3). Fall back to room_idx for older payloads.
        if (a.room_id != null && deletedId != null) return a.room_id !== deletedId;
        return a.room_idx !== idx; // backward-compat
      }).map((a) => {
        // Also keep room_idx in sync for backward-compat payloads that still carry it.
        if (a.room_id != null) return a; // id-keyed — no re-indexing needed
        return a.room_idx > idx ? { ...a, room_idx: a.room_idx - 1 } : a;
      }),
    })));
    setInputsVersion((v) => v + 1);
  }, []);

  // Bulk delete rooms by indices (descending order keeps earlier indices stable).
  // Primary identifier: room.id (stable UUID). room_idx re-indexing is kept
  // as a backward-compat fallback for payloads that pre-date schema_version 3.
  const handleBulkDeleteRooms = useCallback((indices) => {
    const toDelete = new Set(indices);
    // Collect the stable ids of rooms being deleted (schema v3 payloads).
    const deletedIds = new Set(
      indices.map((i) => rooms[i]?.id).filter(Boolean)
    );
    // Build a mapping from old idx → new idx for the backward-compat path.
    let offset = 0;
    const idxMap = new Map();
    rooms.forEach((_, i) => {
      if (toDelete.has(i)) {
        offset += 1;
        idxMap.set(i, null); // deleted
      } else {
        idxMap.set(i, i - offset);
      }
    });
    setRooms((prev) => prev.filter((_, i) => !toDelete.has(i)));
    setResults((prev) => prev.filter((_, i) => !toDelete.has(i)));
    setConfigurations((prev) => (prev || []).map((cfg) => ({
      ...cfg,
      assignments: (cfg.assignments || [])
        .filter((a) => {
          // Prefer room_id (schema v3). Fall back to room_idx for older payloads.
          if (a.room_id != null && deletedIds.size > 0) return !deletedIds.has(a.room_id);
          return idxMap.get(a.room_idx) !== null; // backward-compat
        })
        .map((a) => {
          if (a.room_id != null) return a; // id-keyed — no re-indexing needed
          return { ...a, room_idx: idxMap.get(a.room_idx) }; // backward-compat
        }),
    })));
    setInputsVersion((v) => v + 1);
  }, [rooms]);

  if (guestRedirect) return <Navigate to="/login" replace />;

  return (
    <div className="container mx-auto py-6 px-4 xl:px-10 max-w-[1920px]">
      <div className="print:hidden">
        <AppHeader
          title="Selection Wizard"
          subtitle={`Step ${step + 1} of ${STEPS.length}${autoSaving ? " · Saving…" : (user && currentProjectId ? " · ✓ Saved" : "")}`}
          projectName={project?.name?.trim() || null}
          rightExtras={
            <TooltipProvider delayDuration={150}>
              {step >= 1 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="flex items-center gap-2 text-xs px-2.5 py-1 rounded border border-border bg-card"
                      data-testid="header-unit-toggle"
                    >
                      <span className={`font-medium ${!isSI ? "text-foreground" : "text-muted-foreground"}`}>IP</span>
                      <Switch
                        checked={isSI}
                        onCheckedChange={(checked) => {
                          setIsSI(checked);
                        }}
                        data-testid="header-unit-toggle-switch"
                        aria-label="Toggle IP/SI"
                      />
                      <span className={`font-medium ${isSI ? "text-foreground" : "text-muted-foreground"}`}>SI</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Toggle Imperial / Metric — affects displays and the Rooms template</TooltipContent>
                </Tooltip>
              )}
              {user && (
                <SaveLoadProject
                  getState={getWizardState}
                  setState={setWizardState}
                  currentName={project.name}
                  setCurrentName={(n) => setProject((p) => ({ ...p, name: n }))}
                  currentId={currentProjectId}
                  setCurrentId={setCurrentProjectId}
                />
              )}
              {!authLoading && !user && (
                <div className="flex items-center gap-2 text-sm" data-testid="guest-bar">
                  <Link
                    to="/login"
                    className="text-foreground hover:text-primary underline"
                    data-testid="header-login-link"
                  >
                    Sign in
                  </Link>
                  <span className="text-muted-foreground">|</span>
                  <Link
                    to="/register"
                    className="text-foreground hover:text-primary underline"
                    data-testid="header-register-link"
                  >
                    Register
                  </Link>
                </div>
              )}
            </TooltipProvider>
          }
        />
      </div>

      <Tabs value={STEPS[step].key} className="space-y-4">
        <TabsList className="grid grid-cols-8 print:hidden h-auto p-1" data-testid="wizard-tabs">
          {STEPS.map((s, i) => {
            const isCompleted = i < step;
            const isActive = i === step;
            const isFuture = i > step;
            return (
              <TabsTrigger
                key={s.key}
                value={s.key}
                onClick={() => {
                  // Backward jumps are always free. Forward jumps require every
                  // intermediate step to pass validation — prevents skipping
                  // required fields (e.g. jumping from Step 0 to Summary without
                  // entering rooms or design conditions).
                  const targetReachable = i <= step
                    ? true
                    : Array.from({ length: i }, (_, k) => k).every((k) => validateStep(k));
                  if (targetReachable) {
                    setStep(i);
                  }
                }}
                data-testid={`wizard-tab-${s.key}`}
                className={`text-xs gap-1.5 ${isFuture ? "text-muted-foreground/60" : ""}`}
              >
                {isCompleted ? (
                  <Check className="h-3 w-3 text-primary shrink-0" />
                ) : (
                  <span
                    className={`font-mono text-[10px] tabular-nums shrink-0 ${
                      isActive ? "text-primary font-semibold" : "opacity-60"
                    }`}
                  >
                    {i + 1}
                  </span>
                )}
                <span className="truncate">{s.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Wizard Navigation Toolbar */}
        <div className="flex items-center justify-between print:hidden border-b border-border pb-3 mb-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={goBack}
              disabled={step === 0}
              data-testid="wizard-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step === 1 && (
              <>
                <Button
                  type="button"
                  onClick={() => setRooms((r) => [...r, blankRoom(r[r.length - 1]?.unit_model)])}
                  data-testid="wizard-add-room-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Room
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="wizard-upload-btn"
                  title="Upload Excel/CSV"
                >
                  <Upload className="h-4 w-4 mr-1" /> Import from Excel/CSV
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => (isSI ? downloadRoomsTemplateSI() : downloadRoomsTemplate())}
                  data-testid="wizard-template-btn"
                  title={`Download a blank ${isSI ? "SI" : "IP"} template`}
                >
                  <Download className="h-4 w-4 mr-1" /> {isSI ? "SI" : "IP"} Template
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleUploadFile}
                  className="hidden"
                  data-testid="wizard-upload-input"
                />
              </>
            )}
          </div>
          {/* Next button — hidden on Selection Criteria (step 3) which shows
              the Perform Selection button instead, and on the last step. */}
          {step < STEPS.length - 1 && step !== 3 && (
            <div className="flex items-center gap-2">
              {/* Summary (step 4): must generate Selection Report before continuing */}
              {step === 4 && selectionMode === "accepted" && !reports.selection && (
                <span className="text-xs text-foreground bg-muted border border-border px-2 py-1 rounded">
                  Generate the <strong>Selection Report</strong> first to continue.
                </span>
              )}
              {/* Schedule (step 6): must generate Config Report before continuing */}
              {step === 6 && !reports.config && (
                <span className="text-xs text-foreground bg-muted border border-border px-2 py-1 rounded">
                  Generate the <strong>Config Report</strong> first to continue.
                </span>
              )}
              <Button
                onClick={goNext}
                disabled={(step === 4 && selectionMode === "accepted" && !reports.selection) || (step === 6 && !reports.config)}
                data-testid="wizard-next-btn"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
          {/* Perform Selection — shown on Selection Criteria tab (step 3) */}
          {step === 3 && (
            <Button
              onClick={goNext}
              disabled={running}
              data-testid="wizard-perform-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {running ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <PlayCircle className="h-4 w-4 mr-1" />
              )}
              {running ? "Running..." : "Perform Selection"}
            </Button>
          )}
          {step === STEPS.length - 1 && (
            <Button
              onClick={async () => {
                if (!user) {
                  toast.error("Sign in to save your project");
                  return;
                }
                try {
                  await autoSaveFnRef.current?.();
                  toast.success("Project saved");
                } catch (e) {
                  toast.error("Save failed: " + (e?.message || e));
                }
              }}
              disabled={autoSaving}
              data-testid="wizard-save-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {autoSaving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              {autoSaving ? "Saving…" : "Save"}
            </Button>
          )}
        </div>

        <TabsContent value="project">
          <ProjectInfoTab project={project} updateProject={setProject} />
        </TabsContent>
        <TabsContent value="rooms">
          <RoomDetailsTab
            rooms={rooms}
            setRooms={setRooms}
            isSI={isSI}
            onRemoveRoom={handleRemoveRoom}
            onBulkDeleteRooms={handleBulkDeleteRooms}
            updateRoomQty={updateRoomQty}
            results={results}
          />
        </TabsContent>
        <TabsContent value="design">
          <DesignConditionsTab
            project={project}
            updateProject={setProject}
            rooms={rooms}
            isSI={isSI}
            onMaxLengthChange={() => {
              if (!results || !results.length) return;
              if (capDebounceRef.current) clearTimeout(capDebounceRef.current);
              capDebounceRef.current = setTimeout(() => {
                if (runAllRef.current) runAllRef.current();
              }, 800);
            }}
          />
        </TabsContent>
        {/* B.1.6 — Selection Criteria tab: Capacity Basis, Speed Ceiling, FACB/ACB Strategy */}
        <TabsContent value="criteria">
          <SelectionCriteriaTab
            project={project}
            updateProject={setProject}
            rooms={rooms}
            isSI={isSI}
          />
        </TabsContent>
        <TabsContent value="summary">
          <SummaryTab
            project={{ ...project, id: currentProjectId }}
            rooms={rooms}
            results={results}
            configurations={configurations}
            isSI={isSI}
            selectionMode={selectionMode}
            running={running}
            resultsStale={resultsStale}
            onRerunSelection={() => runAll(false)}
            onOptimize={() => runAll(true)}
            onAccept={() => {
              setSelectionMode("accepted");
              toast.success("Selection accepted");
            }}
            onIndividual={() => {
              setSelectionMode("individual");
              toast.info("Use the length dropdown next to each unit to pick manually.");
            }}
            onPdfGenerated={(kind) => {
              // Refresh the persisted reports cache so the Submittal tab sees
              // the newly-stored PDF and the Next button gates unlock.
              if (kind && currentProjectId) refreshReports(currentProjectId);
              if (user) autoSave();
            }}
            onSave={user ? autoSave : undefined}
            selectionReport={reports.selection}
            onUpdatePlan={(roomIdx, newUnits) => {
              setResults((prev) => {
                const next = [...prev];
                const cur = next[roomIdx];
                if (!cur?.ok) return prev;
                const totalQty = newUnits.reduce((s, u) => s + (u.qty || 1), 0);
                const firstWithResult = newUnits.find((u) => u.result?.ok) || newUnits[0];
                next[roomIdx] = {
                  ...cur,
                  summary: {
                    // Use the first sub-unit's result as the "row-level" per-unit view;
                    // totals aggregate via plan.units[] across the schedule.
                    ...(firstWithResult?.result?.summary || cur.summary),
                    plan: {
                      qty: totalQty,
                      length_ft: firstWithResult?.length_ft || cur.summary.plan?.length_ft,
                      per_unit_total: firstWithResult?.per_unit_total || cur.summary.plan?.per_unit_total,
                      per_unit_target: firstWithResult?.per_unit_target,
                      applied: totalQty > 1,
                      overridden: true,
                      units: newUnits,
                    },
                  },
                };
                return next;
              });
            }}
            onUpdateQty={updateRoomQty}
            onSetDefaultQtyMode={(mode) => {
              // Persisted onto `project.default_qty_mode` — auto-save catches
              // it within ~600ms via the debounced effect so future QTY > 1
              // commits in any room use this mode silently.
              setProject((p) => ({ ...p, default_qty_mode: mode }));
            }}
            rowLoading={rowLoading}
            onUpdateRoomStaticPressure={async (roomIdx, value) => {
              // Store the new SP on the room immediately so it round-trips into
              // future payloads (bulk Fill SP, re-run Selection, etc.).
              setRooms((prev) => {
                const next = [...prev];
                next[roomIdx] = { ...next[roomIdx], pa_static_pressure_inwg: value };
                return next;
              });
              // Re-run the selection so the displayed capacities reflect the
              // new SP (especially relevant for ACB nozzle curves and FACB duty).
              const room = { ...roomsRef.current[roomIdx], pa_static_pressure_inwg: value };
              if (!room?.unit_model || isModelN(room.unit_model)) {
                if (user) setTimeout(() => autoSaveLatest(), 100);
                return;
              }
              const curSP = resultsRef.current?.[roomIdx];
              const lockLenSP = curSP?.summary?.selected_length_ft ?? curSP?.summary?.plan?.length_ft ?? null;
              setRowBusy(roomIdx, true);
              try {
                const out = await bumpForStaticPressure(projectRef.current, room, value, lockLenSP);
                if (out?.ok !== false && out?.raw && out?.summary) {
                  setResults((prev) => {
                    const next = [...prev];
                    const cur = next[roomIdx];
                    if (!cur?.ok) return prev;
                    next[roomIdx] = {
                      ...out,
                      ok: true,
                      summary: {
                        ...out.summary,
                        plan: {
                          ...(cur.summary?.plan || {}),
                          ...(out.summary?.plan || {}),
                          overridden: cur.summary?.plan?.overridden ?? false,
                          // CRITICAL: clear each unit's cached per-unit
                          // result snapshot. roomAggregate (SummaryTab)
                          // prefers `units[i].result?.summary` over the
                          // top-level summary; without this clear it
                          // displays the STALE pre-bump water flow / SP /
                          // capacity even though the engine just produced
                          // fresh modulated values. Mirrors the pattern in
                          // onUpdateLength.
                          units: (cur.summary?.plan?.units || []).map((u) => ({ ...u, result: null })),
                        },
                      },
                    };
                    return next;
                  });
                }
              } catch (e) {
                // Non-fatal: the stored SP value is still correct for next full re-run.
              } finally {
                setRowBusy(roomIdx, false);
                if (user) setTimeout(() => autoSaveLatest(), 100);
              }
            }}
            onUpdateRoomPAFlow={async (roomIdx, newCfm) => {
              // Per-row PA flow override (ACB-only). Persists on the room as
              // oa_flow_cfm so future re-runs use the new value, then bumps
              // the row's selection with acb_strategy pinned to
              // given_flow_given_sp (see bumpForPAFlow). Mirrors the SP
              // handler — keep both in sync.
              const room = roomsRef.current?.[roomIdx];
              if (!room?.unit_model || isModelN(room.unit_model)) return;
              const curPA = resultsRef.current?.[roomIdx];
              const lockLenPA = curPA?.summary?.selected_length_ft ?? curPA?.summary?.plan?.length_ft ?? null;
              setRooms((prev) => {
                const next = [...prev];
                next[roomIdx] = {
                  ...next[roomIdx],
                  oa_flow_cfm: newCfm,
                  exhaust_flow_cfm: newCfm,
                };
                return next;
              });
              setRowBusy(roomIdx, true);
              try {
                const out = await bumpForPAFlow(projectRef.current, { ...room, oa_flow_cfm: newCfm, exhaust_flow_cfm: newCfm }, newCfm, lockLenPA);
                if (out?.ok !== false && out?.raw && out?.summary) {
                  setResults((prev) => {
                    const next = [...prev];
                    const cur = next[roomIdx];
                    if (!cur?.ok) return prev;
                    next[roomIdx] = {
                      ...out,
                      ok: true,
                      summary: {
                        ...out.summary,
                        plan: {
                          ...(cur.summary?.plan || {}),
                          ...(out.summary?.plan || {}),
                          overridden: cur.summary?.plan?.overridden ?? false,
                          // CRITICAL: clear each unit's cached per-unit
                          // result snapshot. roomAggregate (SummaryTab)
                          // prefers `units[i].result?.summary` over the
                          // top-level summary; without this clear it
                          // displays the STALE pre-bump water flow / SP /
                          // capacity even though the engine just produced
                          // fresh modulated values. Mirrors the pattern in
                          // onUpdateLength.
                          units: (cur.summary?.plan?.units || []).map((u) => ({ ...u, result: null })),
                        },
                      },
                    };
                    return next;
                  });
                  toast.success(`PA flow updated to ${parseFloat(newCfm).toFixed(0)} CFM`);
                }
              } catch (e) {
                toast.error("PA flow update failed: " + (e.message || "unknown"));
              } finally {
                setRowBusy(roomIdx, false);
                if (user) setTimeout(() => autoSaveLatest(), 100);
              }
            }}
            onUpdateRoomSpigot={async (roomIdx, newBoreIn) => {
              // Per-row spigot / air-inlet bore override (ACB-only). Persists
              // on the room as air_inlet_size so the value round-trips into
              // future selections, ordering codes, and PDF. Re-runs the row
              // at the locked length to get fresh NC immediately. For
              // spigot-dependent models (C/C1/D) NC may shift markedly; for
              // flow-only ACBs (H, I-*, L-*) NC is unchanged — by design.
              const room = roomsRef.current?.[roomIdx];
              if (!room?.unit_model || isModelN(room.unit_model)) return;
              const bore = parseInt(newBoreIn, 10);
              if (![4, 6, 8, 10].includes(bore)) return;
              const curSpg = resultsRef.current?.[roomIdx];
              const lockLenSpg =
                curSpg?.summary?.selected_length_ft ??
                curSpg?.summary?.plan?.length_ft ??
                null;
              setRooms((prev) => {
                const next = [...prev];
                next[roomIdx] = { ...next[roomIdx], air_inlet_size: bore };
                return next;
              });
              setRowBusy(roomIdx, true);
              try {
                const out = await bumpForSpigot(
                  projectRef.current,
                  { ...room, air_inlet_size: bore },
                  bore,
                  lockLenSpg
                );
                if (out?.ok !== false && out?.raw && out?.summary) {
                  setResults((prev) => {
                    const next = [...prev];
                    const cur = next[roomIdx];
                    if (!cur?.ok) return prev;
                    next[roomIdx] = {
                      ...out,
                      ok: true,
                      summary: {
                        ...out.summary,
                        plan: {
                          ...(cur.summary?.plan || {}),
                          ...(out.summary?.plan || {}),
                          overridden: cur.summary?.plan?.overridden ?? false,
                          // CRITICAL: clear each unit's cached per-unit
                          // result snapshot. roomAggregate (SummaryTab)
                          // prefers `units[i].result?.summary` over the
                          // top-level summary; without this clear it
                          // displays the STALE pre-bump water flow / SP /
                          // capacity even though the engine just produced
                          // fresh modulated values. Mirrors the pattern in
                          // onUpdateLength.
                          units: (cur.summary?.plan?.units || []).map((u) => ({ ...u, result: null })),
                        },
                      },
                    };
                    return next;
                  });
                  toast.success(`Air inlet updated to ${bore}"`);
                }
              } catch (e) {
                toast.error("Air inlet update failed: " + (e.message || "unknown"));
              } finally {
                setRowBusy(roomIdx, false);
                if (user) setTimeout(() => autoSaveLatest(), 100);
              }
            }}
            onUpdateRoomWaterFlow={async (roomIdx, newGpm) => {
              const room = roomsRef.current?.[roomIdx];
              if (!room?.unit_model || isModelN(room.unit_model)) return;
              const cur = resultsRef.current?.[roomIdx];
              // Pin to the row's current length so the engine doesn't silently
              // escalate length when the user just wanted to change flow at
              // THIS coil. Without this pin, typing a higher flow on an
              // under-delivering L=3 lets the engine bisect across all lengths
              // and report L=4's modulated flow (e.g. user types 2.4 → gets
              // 1.29) with the L=3 label still showing — confusing.
              const lockLen = cur?.summary?.selected_length_ft ?? cur?.summary?.plan?.length_ft ?? null;
              setRowBusy(roomIdx, true);
              try {
                const out = await bumpForWaterFlow(projectRef.current, room, newGpm, lockLen);
                if (out?.ok !== false && out?.raw && out?.summary) {
                  setResults((prev) => {
                    const next = [...prev];
                    const cur = next[roomIdx];
                    if (!cur?.ok) return prev;
                    next[roomIdx] = {
                      ...out,
                      ok: true,
                      summary: {
                        ...out.summary,
                        plan: {
                          ...(cur.summary?.plan || {}),
                          ...(out.summary?.plan || {}),
                          overridden: cur.summary?.plan?.overridden ?? false,
                          // CRITICAL: clear each unit's cached per-unit
                          // result snapshot. roomAggregate (SummaryTab)
                          // prefers `units[i].result?.summary` over the
                          // top-level summary; without this clear it
                          // displays the STALE pre-bump water flow / SP /
                          // capacity even though the engine just produced
                          // fresh modulated values. Mirrors the pattern in
                          // onUpdateLength.
                          units: (cur.summary?.plan?.units || []).map((u) => ({ ...u, result: null })),
                        },
                      },
                    };
                    return next;
                  });
                  toast.success(`Water flow updated to ${parseFloat(newGpm).toFixed(2)} GPM`);
                }
              } catch (e) {
                toast.error("Water flow update failed: " + (e.message || "unknown"));
              } finally {
                setRowBusy(roomIdx, false);
                if (user) setTimeout(() => autoSaveLatest(), 100);
              }
            }}
            onUpdateRoomNotes={(roomIdx, value) => {
              // PDF-3 — per-room engineer notes, entered on Summary tab review
              // panel. Persisted as room.notes via auto-save (round-trips through
              // auth_projects.payload which accepts arbitrary room fields).
              setRooms((prev) => {
                const next = [...prev];
                next[roomIdx] = { ...next[roomIdx], notes: value };
                return next;
              });
              if (user) setTimeout(() => autoSaveLatest(), 100);
            }}
            onUpdateLength={async (roomIdx, newLength, newCircuits) => {
              // Re-run the iterative water-flow bump for the user-chosen
              // length so delivered ≥ target (no negative values when
              // physically possible). Falls back to the cached-results
              // reorder approach if the bump call fails for any reason.
              // `newCircuits` (optional): when set, pins the result to a
              // specific circuit count (for models that expose both 1-ckt and
              // 2-ckt options at the same length).
              // Use refs (not closed-over state) so the handler always sees
              // the latest room / result / project — matches the pattern used
              // by onUpdateRoomStaticPressure / onUpdateRoomWaterFlow.
              const room = roomsRef.current?.[roomIdx];
              const cur = resultsRef.current?.[roomIdx];
              const projectSnap = projectRef.current;
              if (!room || !cur?.ok) return;
              setRunning(true);
              setRowBusy(roomIdx, true);
              try {
                const out = await bumpForLength(projectSnap, room, newLength);
                if (out && out.raw && out.summary) {
                  // Reorder raw.results so chosen length is first (matches
                  // the previous behaviour for downstream PDF / chart consumers).
                  // When newCircuits is specified, also prioritise the matching circuit row.
                  const matchRow = (r) => r.length_ft === newLength && (newCircuits == null || r.circuits === newCircuits);
                  const reordered = [
                    ...out.raw.results.filter(matchRow),
                    ...out.raw.results.filter((r) => !matchRow(r)),
                  ];
                  const newRaw = { ...out.raw, results: reordered };
                  const newSummary = extractMultiLengthSummary(newRaw, room, projectSnap, newLength, newCircuits);
                  setResults((prev) => {
                    const next = [...prev];
                    const oldPlan = cur.summary?.plan || {};
                    // Rewrite EVERY sub-unit to the new length so downstream
                    // logic (configurator cleanup, schedule export) doesn't
                    // think the room still has stale shorter / longer units.
                    // CRITICAL: clear `u.result` from each unit. The unit's
                    // `result` field is the AUTO-SELECT snapshot taken at run
                    // time (length-X data). After the user picks a different
                    // length, that stored result is stale — but roomAggregate
                    // in SummaryTab reads `u.result?.summary` in preference to
                    // the top-level summary, so the displayed cooling/sens/lat
                    // values would stay pinned to the original length even
                    // though the dropdown label and selected_length_ft moved.
                    // Setting result=null makes roomAggregate fall back to the
                    // freshly-extracted summary for the new length.
                    const newUnits = (oldPlan.units && oldPlan.units.length)
                      ? oldPlan.units.map((u) => ({
                          ...u,
                          length_ft: newLength,
                          circuits: newCircuits ?? u.circuits,
                          per_unit_total: newSummary.cooling_total_btuh,
                          result: null,
                        }))
                      : [{
                          model: room.unit_model,
                          length_ft: newLength,
                          circuits: newCircuits ?? null,
                          qty: 1,
                          per_unit_target: parseFloat(room.cooling_total_btuh) || parseFloat(room.cooling_sensible_btuh) || 0,
                          per_unit_total: newSummary.cooling_total_btuh,
                          per_unit_oa: parseFloat(room.oa_flow_cfm) || 0,
                          result: null,
                        }];
                    next[roomIdx] = {
                      ...cur,
                      raw: newRaw,
                      summary: {
                        ...newSummary,
                        plan: {
                          ...oldPlan,
                          length_ft: newLength,
                          per_unit_total: newSummary.cooling_total_btuh,
                          units: newUnits,
                          overridden: true,
                        },
                      },
                    };
                    return next;
                  });
                  toast.success(`Re-modulated for ${newLength} ft`);
                } else {
                  // Fallback: reorder cached results without re-bumping.
                  setResults((prev) => {
                    const next = [...prev];
                    const c = next[roomIdx];
                    if (!c?.ok || !c.raw?.results) return prev;
                    const matchRow = (r) => r.length_ft === newLength && (newCircuits == null || r.circuits === newCircuits);
                    const reordered = [
                      ...c.raw.results.filter(matchRow),
                      ...c.raw.results.filter((r) => !matchRow(r)),
                    ];
                    const newRaw = { ...c.raw, results: reordered };
                    const newSummary = extractMultiLengthSummary(newRaw, room, projectSnap, newLength, newCircuits);
                    next[roomIdx] = {
                      ...c,
                      raw: newRaw,
                      summary: {
                        ...newSummary,
                        plan: {
                          ...(c.summary?.plan || {}),
                          length_ft: newLength,
                          per_unit_total: newSummary.cooling_total_btuh,
                          overridden: true,
                        },
                      },
                    };
                    return next;
                  });
                }
              } catch (e) {
                toast.error("Length change failed: " + (e.message || "unknown"));
              } finally {
                setRunning(false);
                setRowBusy(roomIdx, false);
                if (user) setTimeout(() => autoSaveLatest(), 100);
              }
            }}
          />
        </TabsContent>
        <TabsContent value="configurator">
          <ConfiguratorTab
            project={project}
            configurations={configurations}
            setConfigurations={(next) => {
              setConfigurations(next);
              if (user) setTimeout(() => autoSaveLatest(), 100);
            }}
            rooms={rooms}
            results={results}
            isSI={isSI}
          />
        </TabsContent>
        <TabsContent value="assignment">
          <AssignmentTab
            project={{ ...project, id: currentProjectId }}
            configurations={configurations}
            setConfigurations={(next) => {
              setConfigurations(next);
              if (user) setTimeout(() => autoSaveLatest(), 100);
            }}
            rooms={rooms}
            results={results}
            isSI={isSI}
            onUpdateRoomQty={updateRoomQty}
            onSave={user ? autoSave : undefined}
            configReport={reports.config}
            onReportGenerated={(kind) => {
              if (kind && currentProjectId) refreshReports(currentProjectId);
              if (user) autoSave();
            }}
          />
        </TabsContent>
        <TabsContent value="submittal">
          <SubmittalPackageTab
            project={{ ...project, id: currentProjectId }}
            rooms={rooms}
            results={results}
            configurations={configurations}
            isSI={isSI}
            reports={reports}
            onReportGenerated={(kind) => {
              if (kind && currentProjectId) refreshReports(currentProjectId);
            }}
            onJumpToStep={(targetStep) => setStep(targetStep)}
          />
        </TabsContent>
      </Tabs>

    </div>
  );
}
